import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ──────────── 组织行为潜意识分析库 ──────────── */
interface ProjectionResult {
  phenomenon: string;     // 现象匹配
  psychCause: string;     // 组织心理动因（30字内）
  warning: string;        // 管理者预警信号（30字内）
  cta: string;            // 引导转化
}

const projectionDB: Record<string, ProjectionResult> = {
  '不说话': {
    phenomenon: '会议沉默',
    psychCause: '沉默是对变革不确定性的一种自我保护——团队在用"不发言"避免"说错话"的风险。',
    warning: '此现象持续3周以上，核心成员离职风险将上升15%-20%。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '沉默': {
    phenomenon: '团队沉默',
    psychCause: '沉默是心理安全感崩溃的信号——当表达意见可能带来"惩罚"时，人选择自我封闭。',
    warning: '沉默超过2周，团队创新能力将下降40%，决策质量受损。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '以前': {
    phenomenon: '怀旧抵触',
    psychCause: '怀旧是"丧失感"的防御——老员工在用"以前多好"抵御"现在失控"的焦虑。',
    warning: ' nostalgia 情绪若未被倾听，将在1个月内转化为消极抵抗或离职。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '抵触': {
    phenomenon: '变革抵触',
    psychCause: '抵触不是反对进步，是身份感受到威胁——人抗拒的不是变化，是"被否定"。',
    warning: '抵触情绪放任2周，将形成"反对者联盟"，变革推进成本翻倍。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '离职': {
    phenomenon: '离职潮信号',
    psychCause: '离职是"心理契约破裂"的外在表现——员工感到投入不再被认可或回报。',
    warning: '1人离职后的30天内，团队其他成员离职概率提升3倍。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '累': {
    phenomenon: '团队倦怠',
    psychCause: '倦怠是心理资源枯竭的结果——不是 laziness，是大脑长期处于"威胁模式"。',
    warning: '倦怠持续4周，团队整体效能将下降35%，医疗请假率上升。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '抱怨': {
    phenomenon: '抱怨蔓延',
    psychCause: '抱怨是未被满足需求的变形表达——背后藏着对"被看见"和"被尊重"的渴望。',
    warning: '抱怨若3周内无回应，团队信任度将跌破警戒线，管理者权威受损。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '焦虑': {
    phenomenon: '集体焦虑',
    psychCause: '焦虑在团队中具有传染性——一人不安，全员警觉，这是进化遗留的群体生存机制。',
    warning: '集体焦虑持续发酵，将触发团队"战或逃"反应，决策趋向保守。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '冲突': {
    phenomenon: '人际冲突',
    psychCause: '冲突是边界被侵犯的信号——表面是意见不合，深层是权力或价值感的争夺。',
    warning: '未调解的冲突将在2周内影响跨部门协作，信息流动受阻。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
  '拖延': {
    phenomenon: '执行拖延',
    psychCause: '拖延往往不是 laziness，是潜意识对目标的不认同——"我不想做"变成"我晚一点做"。',
    warning: '目标认同危机若持续，季度业绩达成率可能下降25%以上。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  },
};

const WEWORK_KF_LINK = 'https://work.weixin.qq.com/kfid/kfcc1729b8642e445e3';

function analyzeProjection(text: string): ProjectionResult {
  const lower = text.toLowerCase();
  for (const [keyword, result] of Object.entries(projectionDB)) {
    if (lower.includes(keyword)) return result;
  }
  // 默认回复
  return {
    phenomenon: '团队情绪暗流',
    psychCause: '反复出现的负面现象，往往是团队集体潜意识中焦虑或防御的外显——需要被"命名"才能被"化解"。',
    warning: '未被关注的现象持续4周以上，将固化成团队文化的一部分，纠正成本倍增。',
    cta: '获取该场景下团队干预的【靶向工具箱】',
  };
}

export default function OrganizationalProjectionSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [result, setResult] = useState<ProjectionResult | null>(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    gsap.from(content, {
      y: 40, opacity: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: content, start: 'top bottom-=10%', toggleActions: 'play none none reverse' },
    });
  }, []);

  const handleAnalyze = () => {
    if (!input.trim()) return;
    const r = analyzeProjection(input.trim());
    setResult(r);
    setHasAnalyzed(true);
  };

  return (
    <section
      id="org-projection"
      ref={sectionRef}
      style={{ backgroundColor: '#fcfcfc', padding: '10vmax 5vmax' }}
    >
      <div className="absolute top-0 left-0 right-0 h-px" style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }} />

      <div ref={contentRef} className="max-w-3xl mx-auto">
        {/* 标题 */}
        <div className="text-center mb-12">
          <p className="mb-3" style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.12em' }}>
            组织行为学 · 心理安全感与防御机制
          </p>
          <h2
            className="font-body"
            style={{
              fontSize: 'clamp(1.4rem, 3vw, 2.2rem)',
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#1A2A4A',
              wordBreak: 'keep-all',
              textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.15)',
            }}
          >
            "组织行为潜意识"投射分析
          </h2>
          <p className="mt-3 mx-auto max-w-lg" style={{ fontSize: '0.82rem', lineHeight: 1.7, color: '#6e5e50' }}>
            基于组织行为学中的"心理安全感"与"防御机制"理论，解码团队负面现象背后隐藏的集体焦虑。
          </p>
        </div>

        {/* 输入区 */}
        <div
          className="mb-8"
          style={{
            border: '1.5px solid #d4c4b0',
            borderRadius: '14px',
            backgroundColor: '#fff',
            padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.75rem' }}>
            描述近期团队中的负面现象
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="如：大家开会突然不说话 / 老员工总说以前多好 / 最近团队抱怨变多了..."
            rows={3}
            className="w-full outline-none resize-none mb-4 px-3 py-2"
            style={{
              border: 'none',
              borderBottom: '1.5px solid #d4c4b0',
              backgroundColor: '#faf9f7',
              color: '#2c2420',
              fontSize: '0.85rem',
              lineHeight: 1.7,
              borderRadius: '6px',
            }}
          />
          <div className="flex items-center justify-between">
            <p style={{ fontSize: '0.72rem', color: '#a08e78' }}>
              输入团队观察到的现象或员工的吐槽，AI将解码背后的组织心理动因
            </p>
            <button
              onClick={handleAnalyze}
              className="px-6 py-2.5 transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: '#1A2A4A',
                color: '#fff',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              🔍 解码潜意识
            </button>
          </div>
        </div>

        {/* 输出结果 */}
        {hasAnalyzed && result && (
          <div style={{ animation: 'fadeInUp 0.5s ease-out' }}>
            {/* 匹配的现象 */}
            <div
              className="mb-4 p-4"
              style={{
                background: '#f0f4f8',
                borderRadius: '10px',
                borderLeft: '3px solid #1A2A4A',
              }}
            >
              <p style={{ fontSize: '0.7rem', color: '#a08e78', marginBottom: '0.25rem' }}>识别现象</p>
              <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1A2A4A' }}>{result.phenomenon}</p>
            </div>

            {/* 三部分输出 */}
            <div
              className="p-5"
              style={{
                background: '#fff',
                border: '1.5px solid #e8e0d4',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
              }}
            >
              {/* ① 组织心理动因 */}
              <div className="mb-4">
                <div style={{
                  display: 'inline-block', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                  background: '#1A2A4A', padding: '2px 8px', borderRadius: '4px', marginBottom: '0.4rem',
                }}>
                  组织心理动因
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.7, color: '#2c2420' }}>
                  {result.psychCause}
                </p>
              </div>

              {/* ② 管理者预警 */}
              <div
                className="mb-4 p-3"
                style={{
                  background: '#FDF0F0',
                  borderRadius: '8px',
                  borderLeft: '3px solid #D94A4A',
                }}
              >
                <div style={{
                  display: 'inline-block', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                  background: '#D94A4A', padding: '2px 8px', borderRadius: '4px', marginBottom: '0.4rem',
                }}>
                  ⚠️ 管理者预警
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.7, color: '#5a4a3a' }}>
                  {result.warning}
                </p>
              </div>

              {/* ③ 引导转化 */}
              <div
                className="text-center p-4"
                style={{
                  background: '#fff8f3',
                  borderRadius: '10px',
                  border: '1.5px dashed #E8751A',
                }}
              >
                <p style={{ fontSize: '0.82rem', color: '#E8751A', fontWeight: 600, marginBottom: '0.75rem' }}>
                  {result.cta}
                </p>
                <a
                  href={WEWORK_KF_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-2.5 transition-all duration-200 hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #E8751A, #d46816)',
                    color: '#fff',
                    borderRadius: '9999px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    boxShadow: '0 4px 16px rgba(232,117,26,0.3)',
                  }}
                >
                  点击预约组织顾问 →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* 常见现象提示 */}
        {!hasAnalyzed && (
          <div className="mt-6">
            <p style={{ fontSize: '0.72rem', color: '#a08e78', marginBottom: '0.75rem' }}>常见团队现象</p>
            <div className="flex flex-wrap gap-2">
              {[
                '大家开会突然不说话',
                '老员工总说以前多好',
                'AI重构后员工抵触',
                '核心高管离职后军心动摇',
                '销售冲刺期团队倦怠',
                '最近抱怨变多了',
              ].map(item => (
                <button
                  key={item}
                  className="py-1.5 px-3 transition-all duration-200"
                  style={{
                    background: '#f5f0e8', border: '1px solid #e8e0d4', borderRadius: '8px',
                    fontSize: '0.75rem', color: '#6e5e50', cursor: 'pointer',
                  }}
                  onClick={() => setInput(item)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#E8751A'; e.currentTarget.style.color = '#E8751A'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e0d4'; e.currentTarget.style.color = '#6e5e50'; }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
